<script lang="ts">
export type ToastSeverity = 'success' | 'info' | 'warning' | 'error'

/** A button under the message; the toast closes once it is pressed */
export type ToastAction = { label: string; onClick: () => void }

export type ToastCardProps = {
  severity: ToastSeverity
  summary: string
  detail?: string
  footerLeft?: string
  footerRight?: string
  action?: ToastAction
}
</script>

<script setup lang="ts">
import IconMaterialSymbolsCheckCircleOutlineRounded from '~icons/material-symbols/check-circle-outline-rounded'
import IconMaterialSymbolsInfoOutlineRounded from '~icons/material-symbols/info-outline-rounded'
import IconMaterialSymbolsWarningOutlineRounded from '~icons/material-symbols/warning-outline-rounded'
import IconMaterialSymbolsErrorOutlineRounded from '~icons/material-symbols/error-outline-rounded'
import Button from '@/shared/components/Button.vue'
import Icon from '@/shared/components/Icon.vue'

import type { Component } from 'vue'

const props = defineProps<ToastCardProps>()

const severityIcon: Record<ToastSeverity, Component> = {
  success: IconMaterialSymbolsCheckCircleOutlineRounded,
  info: IconMaterialSymbolsInfoOutlineRounded,
  warning: IconMaterialSymbolsWarningOutlineRounded,
  error: IconMaterialSymbolsErrorOutlineRounded
}

const severityIconColor: Record<ToastSeverity, string> = {
  success: 'text-success',
  info: 'text-info',
  warning: 'text-warning',
  error: 'text-destructive'
}
</script>

<template>
  <div class="flex w-full flex-col gap-2">
    <div class="flex items-center gap-2">
      <Icon
        class="h-5 w-5"
        :class="severityIconColor[props.severity]"
        :icon="severityIcon[props.severity]"
      />
      <span class="font-semibold">{{ props.summary }}</span>
    </div>

    <slot>
      <span
        v-if="props.detail"
        class="whitespace-pre-line"
        >{{ props.detail }}</span
      >
    </slot>

    <div
      v-if="props.footerLeft || props.footerRight"
      class="text-muted-foreground flex items-start justify-between gap-2 text-sm"
    >
      <span class="whitespace-pre-line">{{ props.footerLeft }}</span>
      <span class="shrink-0">{{ props.footerRight }}</span>
    </div>

    <div
      v-if="props.action"
      class="flex justify-end"
    >
      <Button
        :label="props.action.label"
        size="small"
        severity="secondary"
        @click="props.action.onClick"
      />
    </div>
  </div>
</template>
